<template>
    <div>
      <AdminHeader />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Edit Store</h5>
          <p class="page-sub">Update store details and location</p>
        </div>
        <router-link :to="$url('/manage-store')" class="btn-outline">
          <font-awesome-icon icon="arrow-left" /> Back
        </router-link>
      </div>
  
      <!-- Validation Errors -->
      <div v-if="validationErrors?.length" class="error-banner">
        <font-awesome-icon icon="exclamation-circle" class="error-icon" />
        <ul class="mb-0 ps-1">
          <li v-for="(err, i) in validationErrors" :key="i">{{ err }}</li>
        </ul>
      </div>
  
      <!-- Skeleton -->
      <div v-if="pageLoading" class="form-card">
        <div class="skeleton-grid">
          <div class="skeleton skeleton--field" v-for="n in 6" :key="n"></div>
        </div>
      </div>
  
      <!-- Form Card -->
      <div v-else class="form-card">
        <form @submit.prevent="editStore">
  
          <!-- Store badge -->
          <div class="store-badge">
            <div class="store-badge-icon">
              <font-awesome-icon icon="store" />
            </div>
            <div>
              <p class="store-badge-name">{{ store_name || 'Store' }}</p>
              <p class="store-badge-address">{{ address || 'No address set' }}</p>
            </div>
          </div>
  
          <div class="section-divider"></div>
  
          <!-- Store Details -->
          <div class="section-header">
            <div class="section-icon section-icon--teal">
              <font-awesome-icon icon="store" />
            </div>
            <div>
              <p class="section-title">Store Details</p>
              <p class="section-sub">Update the store's name and contact</p>
            </div>
          </div>
  
          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">Store Name <span class="req">*</span></label>
              <input v-model="store_name" type="text" class="field-input" placeholder="Enter store name" />
            </div>
            <div class="field-group">
              <label class="field-label">Phone Number</label>
              <input v-model="phone_no" type="text" class="field-input" placeholder="e.g. 08012345678" />
            </div>
            <div class="field-group fields-col-2">
              <label class="field-label">Address</label>
              <input v-model="address" type="text" class="field-input" placeholder="Enter store address" />
            </div>
          </div>
  
          <div class="section-divider"></div>
  
          <!-- Location -->
          <div class="section-header">
            <div class="section-icon section-icon--orange">
              <font-awesome-icon icon="map-marker-alt" />
            </div>
            <div>
              <p class="section-title">Location</p>
              <p class="section-sub">Update country, state and city</p>
            </div>
          </div>
  
          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">Country</label>
              <select v-model="country_id" class="field-input field-select" @change="getStates($event)">
                <option value="">Choose country</option>
                <option :value="c.id" v-for="c in countries" :key="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">State</label>
              <select v-model="state_id" class="field-input field-select" @change="getCities($event)" :disabled="!states?.length">
                <option value="">Choose state</option>
                <option :value="s.id" v-for="s in states" :key="s?.id">{{ s?.name }}</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">City</label>
              <select v-model="city_id" class="field-input field-select" :disabled="!cities?.length">
                <option value="">Choose city</option>
                <option :value="c.id" v-for="c in cities" :key="c?.id">{{ c?.name }}</option>
              </select>
            </div>
          </div>
  
          <!-- Footer -->
          <div class="form-footer">
            <router-link :to="$url('/manage-store')" class="btn-outline">Cancel</router-link>
            <button type="submit" class="btn-primary-action" :disabled="crudLoading">
              <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <font-awesome-icon v-else icon="check" class="me-2" />
              {{ crudLoading ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
  
        </form>
      </div>
  
    </div>
  </template>
  
  <script>
  import { ref } from "@vue/reactivity";
  import AdminHeader from "./components/settingheadertab.vue";
  import axios from "axios";
  import { useToast } from "vue-toastification";
  import { useRoute, useRouter } from "vue-router";
  import { onMounted } from "@vue/runtime-core";
  
  export default {
    components: { AdminHeader },
  
    setup() {
      const store_name        = ref('');
      const phone_no          = ref('');
      const address           = ref('');
      const countries         = ref([]);
      const cities            = ref([]);
      const states            = ref([]);
      const country_id        = ref('');
      const city_id           = ref('');
      const state_id          = ref('');
      // const loading           = ref(false);
      const pageLoading       = ref(true);
      const toast             = useToast();
      const router            = useRouter();
      const validationErrors  = ref('');
      const permission_status = ref(null);
      const crudLoading       = ref(false);
      const { params }        = useRoute();
  
      const singleStore = async () => {
        pageLoading.value = true;
        try {
          const res      = await axios.get(`/admin/store/${params.id}`);
          const thestore = res.data.data.store;
          store_name.value = thestore.store_name;
          phone_no.value   = thestore.phone_no;
          address.value    = thestore.address;
          city_id.value    = thestore.city_id;
          state_id.value   = thestore.state_id;
          country_id.value = thestore.country_id;
          states.value     = res.data.data.states;
          cities.value     = res.data.data.cities;
        } finally {
          pageLoading.value = false;
        }
      };
  
      const editStore = async () => {
        crudLoading.value = true;
        axios.put(`/admin/store/${params.id}`, {
          store_name: store_name.value,
          phone_no:   phone_no.value,
          address:    address.value,
          city_id:    city_id.value,
          state_id:   state_id.value,
          country_id: country_id.value,
        }).then(() => {
          crudLoading.value = false;
          router.push('/manage-store');
          toast.success('Store updated successfully');
        }).catch(error => {
          crudLoading.value = false;
          if (error.response.status == 422) {
            toast.warning('Your inputs are not valid, kindly recheck');
            validationErrors.value = Array.isArray(error.response.data.message) ? error.response.data.message : (error.response.data.message ? [String(error.response.data.message)] : []);
          }
          if (error.response.status == 500) toast.error('An error occurred, please try again');
          if (error?.response?.status == 403) {
            permission_status.value = 403;
            toast.error(error.response.data.message);
          }
        });
      };
  
      const getAllCountries = async () => {
        const res = await axios.get('countries');
        countries.value = res.data.data.data;
      };
  
      const getStates = async (event) => {
        states.value = []; cities.value = []; state_id.value = ''; city_id.value = '';
        const res = await axios.get('/states/' + event.target.value);
        states.value = res.data.data.data;
      };
  
      const getCities = async (event) => {
        cities.value = []; city_id.value = '';
        const res = await axios.get('/cities/' + event.target.value);
        cities.value = res.data.data.data;
      };
  
      onMounted(() => { singleStore(); getAllCountries(); });
  
      return {
        store_name, phone_no, address,
        countries, states, cities,
        country_id, city_id, state_id,
        validationErrors, editStore,
        getStates, getCities,
        permission_status, crudLoading, pageLoading,
      };
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header {
    display: flex; align-items: flex-start;
    justify-content: space-between; flex-wrap: wrap;
    gap: 12px; margin-bottom: 20px;
  }
  .page-title { font-size: 20px; font-weight: 700; color: #1a2b45; margin: 0 0 3px; }
  .page-sub   { font-size: 13px; color: #7a90b0; margin: 0; }
  
  /* ── Error Banner ── */
  .error-banner {
    display: flex; align-items: flex-start; gap: 10px;
    background: #fff5f5; border: 1px solid #fac5c5;
    border-radius: 10px; padding: 12px 16px;
    margin-bottom: 20px; color: #e03535; font-size: 13px;
  }
  .error-icon { font-size: 16px; margin-top: 2px; flex-shrink: 0; }
  
  /* ── Skeleton ── */
  .skeleton-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 16px; padding: 8px 0; }
  .skeleton {
    border-radius: 8px;
    background: linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%);
    background-size: 200% 100%;
    animation: shimmer 1.4s infinite;
  }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .skeleton--field { height: 52px; }
  
  /* ── Form Card ── */
  .form-card {
    background: #fff; border: 1px solid #e4eaf2;
    border-radius: 12px; padding: 28px;
  }
  
  /* ── Store Badge ── */
  .store-badge {
    display: flex; align-items: center; gap: 14px;
    background: #f8fafc; border: 1px solid #e4eaf2;
    border-radius: 10px; padding: 14px 18px;
  }
  .store-badge-icon {
    width: 46px; height: 46px; border-radius: 10px;
    background: rgba(13,148,136,.1); color: #0d9488;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px; flex-shrink: 0;
  }
  .store-badge-name    { font-size: 14px; font-weight: 700; color: #1a2b45; margin: 0 0 2px; text-transform: capitalize; }
  .store-badge-address { font-size: 12.5px; color: #7a90b0; margin: 0; }
  
  /* ── Divider ── */
  .section-divider { height: 1px; background: #f0f4f8; margin: 22px 0; }
  
  /* ── Section Header ── */
  .section-header { display: flex; align-items: center; gap: 14px; margin-bottom: 18px; }
  .section-icon {
    width: 42px; height: 42px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 17px; flex-shrink: 0;
  }
  .section-icon--teal   { background: rgba(13,148,136,.1); color: #0d9488; }
  .section-icon--orange { background: rgba(224,140,0,.1);  color: #e08c00; }
  .section-title { font-size: 14px; font-weight: 700; color: #1a2b45; margin: 0 0 2px; }
  .section-sub   { font-size: 12px; color: #7a90b0; margin: 0; }
  
  /* ── Fields ── */
  .fields-grid  { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
  .fields-col-2 { grid-column: span 2; }
  .field-group  { display: flex; flex-direction: column; gap: 6px; }
  .field-label  { font-size: 13px; font-weight: 600; color: #1a2b45; }
  .req          { color: #e03535; }
  
  .field-input {
    padding: 9px 12px; border: 1px solid #dde5f0;
    border-radius: 8px; font-size: 13.5px; color: #1a2b45;
    background: #fff; outline: none; width: 100%;
    transition: border-color .15s, box-shadow .15s;
  }
  .field-input:focus { border-color: #004AAD; box-shadow: 0 0 0 3px rgba(0,74,173,.08); }
  .field-input::placeholder { color: #aab8cc; }
  .field-input:disabled { background: #f8fafc; color: #aab8cc; cursor: not-allowed; }
  .field-select { appearance: auto; cursor: pointer; }
  
  /* ── Footer ── */
  .form-footer {
    display: flex; align-items: center; justify-content: flex-end;
    gap: 10px; margin-top: 28px; padding-top: 20px;
    border-top: 1px solid #f0f4f8;
  }
  .btn-primary-action {
    display: inline-flex; align-items: center;
    background: #004AAD; color: #fff;
    font-size: 13px; font-weight: 600;
    padding: 9px 24px; border-radius: 8px; border: none;
    cursor: pointer; transition: background .15s;
  }
  .btn-primary-action:hover:not(:disabled) { background: #003b8e; }
  .btn-primary-action:disabled { opacity: .6; cursor: not-allowed; }
  
  .btn-outline {
    display: inline-flex; align-items: center; gap: 7px;
    background: #fff; color: #1a2b45;
    border: 1px solid #dde5f0; font-size: 13px; font-weight: 500;
    padding: 9px 20px; border-radius: 8px;
    cursor: pointer; text-decoration: none;
    transition: background .15s;
  }
  .btn-outline:hover { background: #f4f7fb; color: #1a2b45; text-decoration: none; }
  
  /* ── Responsive ── */
  @media (max-width: 768px) {
    .fields-grid  { grid-template-columns: 1fr; }
    .fields-col-2 { grid-column: span 1; }
    .skeleton-grid { grid-template-columns: 1fr; }
    .form-card    { padding: 18px; }
    .form-footer  { flex-direction: column; }
    .btn-primary-action, .btn-outline { width: 100%; justify-content: center; }
  }
  </style>